<template>
  <div class="role-form d-flex flex-column">
    <div class="h4 d-flex align-items-center justify-content-between flex-shrink-0">
      <span>
        <i class="icon-role pr-2" />
        {{ name || $t('role.new') }}
      </span>
      <button
        v-if="page === 'permission'"
        type="button"
        class="btn btn-sm btn-outline-secondary permissions-ui-toggle"
        @click="togglePermissionsUiVersion"
      >
        <i class="fas fa-flask pr-1" />
        {{ permissionsUiVersion === 'new' ? $t('permissions.classicView') : $t('permissions.newExperience') }}
      </button>
    </div>
    <div class="flex-shrink-0">
      <HorizontalMenu
        :selected.sync="page"
        :list="rolePages"
      />
    </div>

    <div class="role-form__tab-content">
      <div
        v-if="page === 'details'"
        class="p-2"
      >
        <form
          class="text-left"
          @submit.prevent="save"
        >
          <label class="mb-0 small font-weight-bold">
            {{ $t('general.name') }}
          </label>
          <BFormInput
            v-model="name"
            class="mb-2"
          />
          <div class="mt-3">
            <SaveButton
              :pending="pending"
              :remove-pending="removePending"
              :disabled="submitDisabled"
              :show-remove="!!role"
              @cancel="$emit('close')"
              @remove="remove()"
            />
          </div>
        </form>
      </div>
      <div
        v-else
        class="mt-3 flex-grow-1 min-height-0 d-flex flex-column"
      >
        <RolePermissionsForm
          class="flex-grow-1 min-height-0"
          :role-id="role.id"
          :subject-type="role.subjectType"
          :show-only-role-permission="true"
          :ui-version="permissionsUiVersion"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { handleApiError } from '@/utils/handleApiError';
import HorizontalMenu from '@core/components/menu/HorizontalMenu';
import RolePermissionsForm from '@/components/permissions/RolePermissionsForm';
import { getPermissionsUiPreference, setPermissionsUiPreference } from '@/utils/permissionsUiPreference';

export default {
  props: {
    role: Object,
  },
  data: () => ({
    pending: false,
    removePending: false,
    error: null,
    id: '',
    name: '',
    page: 'details',
    permissionsUiVersion: getPermissionsUiPreference(),
  }),
  components: {
    RolePermissionsForm,
    HorizontalMenu,
  },
  computed: {
    ...mapGetters('oidcStore', ['oidcUser']),
    ...mapGetters(['tenantId']),
    rolePages() {
      let pages = [{
        key: 'details',
        name: this.$t('general.details'),
      }];
      if (this.role) {
        pages = [...pages, {
          key: 'permission',
          name: this.$t('account.permissions'),
        }];
      }
      return pages;
    },
    submitDisabled() {
      return !this.name;
    },
    roleRequestModel() {
      return {
        name: this.name,
      };
    },
  },
  methods: {
    ...mapActions(['createRole', 'updateRole', 'deleteRole']),
    togglePermissionsUiVersion() {
      this.permissionsUiVersion = this.permissionsUiVersion === 'new' ? 'classic' : 'new';
      setPermissionsUiPreference(this.permissionsUiVersion);
    },
    save() {
      if (this.role) {
        this.update();
      } else {
        this.create();
      }
    },
    update() {
      this.pending = true;
      this.updateRole({
        params: {
          id: this.id,
        },
        data: this.roleRequestModel,
      })
        .then(() => {
          this.$emit('close');
        })
        .catch(({ response }) => {
          handleApiError(response);
        })
        .finally(() => {
          this.pending = false;
        });
    },
    create() {
      this.pending = true;
      this.createRole({
        params: {
          tenantId: this.tenantId,
        },
        data: this.roleRequestModel,
      })
        .then(() => {
          this.$emit('close');
        })
        .catch(({ response }) => {
          handleApiError(response);
        })
        .finally(() => {
          this.pending = false;
        });
    },
    remove() {
      this.removePending = true;
      this.deleteRole({
        params: {
          id: this.id,
        },
      })
        .catch(({ response }) => {
          handleApiError(response);
        })
        .finally(() => {
          this.pending = false;
          this.$emit('close');
        });
    },
  },
  created() {
    if (this.role) {
      this.id = this.role.id;
      this.name = this.role.name;
    }
  },
};
</script>

<style>
.permissions-ui-toggle {
  margin-right: 40px;
}

.role-form {
  flex: 1 1 auto;
  min-height: 0;
}

.role-form__tab-content {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}
</style>
